import { useRef } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArchiveBoxXMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';

/**
 * Modale de confirmation LOURDE de la SUPPRESSION des sauvegardes `_original`
 * (« Nettoyage EXIF », fonctionnalité de sûreté).
 *
 * Vraie boîte de dialogue courte (calquée sur `ExifEraseConfirmModal` / `DedupConfirmModal`) :
 * `role="dialog"` + `aria-modal`, focus initial/restauré, verrou de défilement, Échap ferme
 * (sauf pendant l'appel réseau), clic-hors ferme. Le caller l'enveloppe dans un `<Portal>`.
 *
 * Le corps (déjà localisé) décrit précisément ce qui sera supprimé (N sauvegardes,
 * taille) et la portée. La suppression ne touche QUE des `*_original` dont la source
 * existe (jamais un vrai média) — action irréversible.
 */
interface OriginalsCleanupConfirmModalProps {
  /** Corps explicite : ce qui sera supprimé + la portée (déjà localisé). */
  body: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function OriginalsCleanupConfirmModal({
  body,
  busy,
  onConfirm,
  onClose,
}: OriginalsCleanupConfirmModalProps): React.JSX.Element {
  const { t } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y({ dialogRef, initialFocusRef: confirmRef, onEscape: !busy ? onClose : undefined });

  const title = t('exif.originals.confirmTitle');

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ExclamationTriangleIcon className="h-6 w-6 text-danger" aria-hidden="true" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('exif.eraseConfirmClose')}
            aria-label={t('exif.eraseConfirmClose')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          <p className="flex items-start gap-2 rounded-md border border-danger/40 bg-surface-2 p-3 text-sm text-fg">
            <ExclamationTriangleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-danger"
              aria-hidden="true"
            />
            <span>
              <span className="font-medium">{t('exif.eraseConfirmWarning')}</span>
              <span className="mt-1 block text-muted">{body}</span>
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            {t('exif.eraseConfirmCancel')}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
            ) : (
              <ArchiveBoxXMarkIcon className="h-5 w-5" aria-hidden="true" />
            )}
            {busy ? t('exif.eraseConfirmApplying') : t('exif.originals.confirmApply')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
